export type StrengthLevel = 0 | 1 | 2 | 3 | 4;

export interface StrengthReport {
  readonly level: StrengthLevel;
  readonly label: string;
  readonly bits: number;
}

const FRAGMENTS: ReadonlyArray<string> = ["passwort", "password", "qwert", "asdf", "123456", "hallo", "willkommen", "letmein", "admin"];

function labelFor(level: StrengthLevel): string {
  switch (level) {
    case 0:
      return "Sehr schwach";
    case 1:
      return "Schwach";
    case 2:
      return "Okay";
    case 3:
      return "Stark";
    case 4:
      return "Sehr stark";
  }
}

function levelFor(bits: number): StrengthLevel {
  if (bits < 28) {
    return 0;
  }
  if (bits < 44) {
    return 1;
  }
  if (bits < 60) {
    return 2;
  }
  if (bits < 80) {
    return 3;
  }
  return 4;
}

function poolSize(password: string): number {
  let pool = 0;
  if (/[a-z]/.test(password)) {
    pool += 26;
  }
  if (/[A-Z]/.test(password)) {
    pool += 26;
  }
  if (/[0-9]/.test(password)) {
    pool += 10;
  }
  if (/[^a-zA-Z0-9]/.test(password)) {
    pool += 33;
  }
  return Math.max(pool, 1);
}

function hasSequence(password: string): boolean {
  for (let i = 0; i + 2 < password.length; i += 1) {
    const a = password.charCodeAt(i);
    const b = password.charCodeAt(i + 1);
    const c = password.charCodeAt(i + 2);
    if ((b - a === 1 && c - b === 1) || (a - b === 1 && b - c === 1)) {
      return true;
    }
  }
  return false;
}

/** Entropy estimate with simple pattern penalties. Heuristic, not a breach check. */
export function estimateStrength(password: string): StrengthReport {
  if (password.length === 0) {
    return { level: 0, label: labelFor(0), bits: 0 };
  }
  let bits = password.length * Math.log2(poolSize(password));
  if (/(.)\1\1/.test(password)) {
    bits *= 0.7;
  }
  if (hasSequence(password)) {
    bits *= 0.8;
  }
  const lower = password.toLowerCase();
  if (FRAGMENTS.some((fragment: string): boolean => lower.includes(fragment))) {
    bits *= 0.5;
  }
  const distinct = new Set(password).size;
  if (distinct <= 3) {
    bits = Math.min(bits, 20);
  } else if (distinct * 2 < password.length) {
    bits *= 0.6;
  }
  if (password.length < 8) {
    bits = Math.min(bits, 20);
  }
  const level = levelFor(bits);
  return { level, label: labelFor(level), bits: Math.round(bits) };
}
